import React, { useState, useEffect, useCallback } from "react";
import { BarChart3, Search } from "lucide-react";
import {
  BarChart,
  Bar,
  Cell,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { getPropertyAnalytics } from "../services/propertyApiService";
import { toPropertyAnalytics } from "../services/adapters/propertyAnalytics";
import AnalyticsCard from "./analytics/AnalyticsCard";

// Property's analytics, a sibling to Analytics.js rather than a branch inside
// it: motor's page renders static demo data from src/data/claimsData.js,
// whereas this one is driven entirely by GET /get_analytics_data. Every
// average and distribution is computed in adapters/propertyAnalytics.js.

const COLORS = {
  duration: "#3b82f6",
  photos: "#10b981",
  rating: "#8b5cf6",
  completed: "#10b981",
  failed: "#ef4444",
};

const KPI_ACCENTS = {
  total: "#0066cc",
  completed: "#10b981",
  failed: "#ef4444",
  duration: "#3b82f6",
  photos: "#4fb4cf",
  rating: "#8b5cf6",
};

function getLast30DayRange() {
  const today = new Date();
  const from = new Date(today);
  from.setDate(from.getDate() - 30);
  return {
    startDate: from.toISOString().split("T")[0],
    endDate: today.toISOString().split("T")[0],
  };
}

function KpiCard({ label, value, accent, icon }) {
  return (
    <div className="bg-background-secondary rounded-lg border border-border-primary shadow-sm p-4 flex flex-col gap-2">
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center text-base"
        style={{ background: accent + "18" }}
      >
        {icon}
      </div>
      <p className="text-2xl font-bold text-text-primary leading-none">{value}</p>
      <p className="text-xs text-text-secondary font-medium">{label}</p>
    </div>
  );
}

function DistributionChart({ title, data, color }) {
  return (
    <AnalyticsCard title={title}>
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
          <Tooltip cursor={{ fill: "#00000008" }} />
          <Bar dataKey="count" fill={color} radius={[4, 4, 0, 0]} maxBarSize={64} />
        </BarChart>
      </ResponsiveContainer>
    </AnalyticsCard>
  );
}

export default function PropertyAnalytics() {
  const [startDate, setStartDate] = useState(() => getLast30DayRange().startDate);
  const [endDate, setEndDate] = useState(() => getLast30DayRange().endDate);
  // Applied separately from the inputs so typing a date doesn't refetch on
  // every keystroke — the fetch runs when Apply is pressed.
  const [applied, setApplied] = useState(() => getLast30DayRange());

  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [ratingFilter, setRatingFilter] = useState("All");
  const [warningFilter, setWarningFilter] = useState("All");

  const load = useCallback(async (range) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getPropertyAnalytics({
        start_date: range.startDate,
        stop_date: range.endDate,
      });
      setAnalytics(toPropertyAnalytics(response));
    } catch (err) {
      console.error("Error loading property analytics:", err);
      setError("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(applied);
  }, [applied, load]);

  if (loading) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-background-primary">
        <p className="text-gray-600">Loading analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-background-primary">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => load(applied)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { records, totals, durationDistribution, photoDistribution, ratingDistribution, links } =
    analytics || {};

  const statusData = [
    { name: "Completed", value: totals.completed, fill: COLORS.completed },
    { name: "Failed", value: totals.failed, fill: COLORS.failed },
  ].filter((d) => d.value > 0);

  const query = search.trim().toLowerCase();
  const visibleRecords = (records || []).filter((r) => {
    if (query && !`${r.refNum} ${r.address}`.toLowerCase().includes(query)) return false;
    if (ratingFilter === "Rated" && r.rating == null) return false;
    if (ratingFilter === "Unrated" && r.rating != null) return false;
    if (ratingFilter !== "All" && ratingFilter !== "Rated" && ratingFilter !== "Unrated") {
      if (Math.round(r.rating ?? -1) !== Number(ratingFilter)) return false;
    }
    if (warningFilter === "With warnings" && r.warnings.length === 0) return false;
    if (warningFilter === "No warnings" && r.warnings.length > 0) return false;
    return true;
  });

  return (
    <div className="h-full overflow-y-auto bg-background-primary">
      {/* Page header */}
      <div className="bg-background-dark px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BarChart3 className="h-5 w-5 text-primary-light" />
          <div>
            <h1 className="text-sm font-bold text-white uppercase tracking-wide">
              Property Analytics
            </h1>
            <p className="text-xs text-text-secondary mt-0.5">
              {totals.total.toLocaleString()} inspections · {applied.startDate} to {applied.endDate}
            </p>
          </div>
        </div>
      </div>

      <div className="px-6 py-5 space-y-5 max-w-screen-2xl mx-auto">
        {/* Filters */}
        <div className="bg-background-secondary rounded-lg border border-border-primary shadow-sm p-4 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs text-text-secondary mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 border border-border-primary rounded text-sm bg-background-secondary text-text-primary"
            />
          </div>
          <div>
            <label className="block text-xs text-text-secondary mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              min={startDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 border border-border-primary rounded text-sm bg-background-secondary text-text-primary"
            />
          </div>
          <button
            onClick={() => setApplied({ startDate, endDate })}
            className="bg-primary text-white px-5 py-2 rounded text-sm font-semibold hover:bg-[#2680c7]"
          >
            Apply
          </button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <KpiCard label="Total Inspections" value={totals.total.toLocaleString()} accent={KPI_ACCENTS.total} icon="📋" />
          <KpiCard label="Completed" value={totals.completed.toLocaleString()} accent={KPI_ACCENTS.completed} icon="✅" />
          <KpiCard label="Failed" value={totals.failed.toLocaleString()} accent={KPI_ACCENTS.failed} icon="❌" />
          <KpiCard
            label="Avg Duration (min)"
            value={totals.avgDurationMinutes == null ? "—" : totals.avgDurationMinutes.toFixed(1)}
            accent={KPI_ACCENTS.duration}
            icon="⏱️"
          />
          <KpiCard
            label="Avg Photos Uploaded"
            value={totals.avgPhotos == null ? "—" : `≈ ${Math.round(totals.avgPhotos)}`}
            accent={KPI_ACCENTS.photos}
            icon="📷"
          />
          <KpiCard
            label="Avg TriNetra Rating"
            value={totals.avgRating == null ? "—" : totals.avgRating.toFixed(2)}
            accent={KPI_ACCENTS.rating}
            icon="⭐"
          />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          <DistributionChart
            title="Inspection Duration Distribution"
            data={durationDistribution}
            color={COLORS.duration}
          />
          <DistributionChart
            title="Photos Uploaded Distribution"
            data={photoDistribution}
            color={COLORS.photos}
          />
          <DistributionChart
            title="TriNetra Rating Distribution"
            data={ratingDistribution}
            color={COLORS.rating}
          />

          <AnalyticsCard title="Completed / Failed Breakdown">
            {statusData.length === 0 ? (
              <p className="text-sm text-text-secondary py-16 text-center">No inspections in this range.</p>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={statusData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                    >
                      {statusData.map((d, i) => (
                        <Cell key={i} fill={d.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
                <p className="text-sm text-text-secondary mt-2">
                  {totals.failed === 0 ? "No failed cases." : `${totals.failed} failed case(s).`}
                </p>
              </>
            )}
          </AnalyticsCard>
        </div>

        {/* TriNetra link generation summary */}
        {links.total > 0 && (
          <AnalyticsCard title="Link Generated Details">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {[
                { label: "Generated", value: links.total },
                { label: "Completed", value: links.completed },
                { label: "Started, not completed", value: links.startedNotCompleted },
                { label: "Not started", value: links.notStarted },
                { label: "Expired", value: links.expired },
              ].map(({ label, value }) => (
                <div key={label} className="rounded-lg p-3 text-center bg-background-primary">
                  <p className="text-lg font-bold text-text-primary">{value}</p>
                  <p className="text-xs text-text-secondary font-medium mt-0.5">{label}</p>
                </div>
              ))}
            </div>
          </AnalyticsCard>
        )}

        {/* Records */}
        <AnalyticsCard title="Inspection Records">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="flex items-center gap-2 border border-border-primary rounded px-3 py-2 flex-1 min-w-[240px]">
              <Search className="w-4 h-4 text-text-secondary" />
              <input
                type="text"
                placeholder="Filter by ref num or address"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-transparent outline-none text-sm flex-1 text-text-primary"
              />
            </div>
            <select
              value={ratingFilter}
              onChange={(e) => setRatingFilter(e.target.value)}
              className="px-3 py-2 border border-border-primary rounded text-sm bg-background-secondary text-text-primary"
            >
              {["All", "Rated", "Unrated", "1", "2", "3", "4", "5"].map((o) => (
                <option key={o} value={o}>
                  {o === "All" ? "All Ratings" : o.length === 1 ? `★ ${o}` : o}
                </option>
              ))}
            </select>
            <select
              value={warningFilter}
              onChange={(e) => setWarningFilter(e.target.value)}
              className="px-3 py-2 border border-border-primary rounded text-sm bg-background-secondary text-text-primary"
            >
              {["All", "With warnings", "No warnings"].map((o) => (
                <option key={o} value={o}>
                  {o === "All" ? "All Warnings" : o}
                </option>
              ))}
            </select>
            <span className="text-xs text-text-secondary ml-auto">
              {visibleRecords.length} of {records.length}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="bg-primary text-white text-left">
                  {["Address", "Ref Num", "Created At", "Status", "Duration (min)", "Photos", "Rating", "Comment", "Warnings"].map(
                    (h) => (
                      <th key={h} className="px-3 py-2.5 font-semibold text-xs whitespace-nowrap">
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {visibleRecords.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-3 py-10 text-center text-text-secondary">
                      No records match these filters.
                    </td>
                  </tr>
                ) : (
                  visibleRecords.map((r, i) => (
                    <tr key={r.refNum || i} className="border-b border-border-primary last:border-0">
                      <td className="px-3 py-2.5 text-text-primary">{r.address || "—"}</td>
                      <td className="px-3 py-2.5 text-primary font-medium">{r.refNum}</td>
                      <td className="px-3 py-2.5 text-text-primary whitespace-nowrap">
                        {r.createdAt ? r.createdAt.split(" ")[0] : "—"}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="inline-block rounded-full px-2 py-0.5 text-xs font-medium bg-[#dcfce7] text-[#166534]">
                          {r.caseStatus === "passed" ? "Completed" : r.caseStatus || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-text-primary">
                        {r.durationSeconds == null ? "—" : (r.durationSeconds / 60).toFixed(1)}
                      </td>
                      <td className="px-3 py-2.5 text-text-primary">{r.photos}</td>
                      <td className="px-3 py-2.5 text-text-primary whitespace-nowrap">
                        {r.rating == null ? "—" : `★ ${r.rating.toFixed(1)}`}
                      </td>
                      <td className="px-3 py-2.5 text-text-secondary">{r.comment || "—"}</td>
                      <td className="px-3 py-2.5 text-[#9a3412] max-w-[260px]">
                        {r.warnings.length === 0 ? (
                          <span className="text-text-secondary">—</span>
                        ) : (
                          r.warnings.join("; ")
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </AnalyticsCard>
      </div>
    </div>
  );
}
